import { MAX_ZOOM_LEVEL, MIN_ZOOM_LEVEL } from '@/src/pages/address/selector_page/contants';
import { MapCameraFitMode, MapLatLng, MapLatLngBounds } from '@wmfe/mach-pro-components-react';
import * as React from 'react';

export interface IMapMarkerContentProps {
  style?: React.CSSProperties;
}

export default class MapMarkerContent extends React.Component<IMapMarkerContentProps> {
  render() {
    const { style } = this.props;
    return <map-marker-content style={style} {...this.props} />;
  }
}

// 返回对应的边界信息
const mapBoundsIPML = (mapLimitBounds) => {
  const NorthEastLat = mapLimitBounds?.NorthEastLat;
  const NorthEastLng = mapLimitBounds?.NorthEastLng;
  const SouthWestLat = mapLimitBounds?.SouthWestLat;
  const SouthWestLng = mapLimitBounds?.SouthWestLng;

  if (NorthEastLat && NorthEastLng && SouthWestLat && SouthWestLng) {
    const northEast: MapLatLng = { latitude: NorthEastLat, longitude: NorthEastLng };
    const southWest: MapLatLng = { latitude: SouthWestLat, longitude: SouthWestLng };
    const bounds: MapLatLngBounds = { northEast, southWest };
    return bounds;
  } else {
    return undefined;
  }
};

export const mapBounds2Coordinates = (bounds: MapLatLngBounds): MapLatLng[] => {
  const northEast: MapLatLng = bounds.northEast;
  const southWest: MapLatLng = bounds.southWest;
  const northWest: MapLatLng = { latitude: northEast.latitude, longitude: southWest.longitude };
  const southEast: MapLatLng = { latitude: southWest.latitude, longitude: northEast.longitude };

  return [northEast, northWest, southWest, southEast];
};

export const mapBounds2Polylines = (bounds: MapLatLngBounds): MapLatLng[][] => {
  const northEast: MapLatLng = bounds.northEast;
  const southWest: MapLatLng = bounds.southWest;
  const northWest: MapLatLng = { latitude: northEast.latitude, longitude: southWest.longitude };
  const southEast: MapLatLng = { latitude: southWest.latitude, longitude: northEast.longitude };

  return [[northEast, northWest], [northWest, southWest], [southWest, southEast], [southEast, northEast]];
};

// 不带 region, 从罗盘取
export const MapLimitBounds = () => {
  const SLMap = Mach.requireModule('SLMapModule');
  if (!SLMap?.getLimitBounds) {
    console.log('适配低版本');
    // 之前的 HongKongBounds 也是直接返回 undefined
    return undefined;
  }

  const mapLimitBounds = SLMap?.getLimitBounds ? SLMap.getLimitBounds() : {};
  return mapBoundsIPML(mapLimitBounds);
};

// 带 region
export const MapLimitBoundsWithRegion = (region: string = '', lat = 0, lng = 0) => {
  //是否开启地址围栏, 未开启，则不设置地址围栏，返回undefined，
  if (!isOpenAddressFence()) {
    console.log('horn未打开，未开启地址围栏');
    return undefined;
  }

  //测试区域，不设置地址围栏，返回undefined，
  if (lat && lng) {
    const bounds = checkInTestAreaBounds(lat, lng);
    if (bounds) {
      console.log('测试区域地址，未开启地址围栏');
      return undefined;
    }
  }

  //低版本，不设置地址围栏
  const SLMap = Mach.requireModule('SLMapModule');
  if (!SLMap?.getLimitBoundsWithRegion) {
    console.log('当前App为低版本，未开启地址围栏');
    // 之前的 HongKongBounds 也是直接返回 undefined
    return undefined;
  }

  const mapLimitBounds = SLMap.getLimitBoundsWithRegion({
    region,
    latitude: lat,
    longitude: lng
  });

  return mapBoundsIPML(mapLimitBounds);
};

/*
HK
NorthEastLat：22.615131
NorthEastLng: 114.419471
SouthWestLat: 21.776418
outhWestLng: 113.369698

SA
NorthEastLat:33.946911
NorthEastLng:58.487613
SouthWestLa:14.431655
SouthWestLng:31.418744

-林芝经纬度：28.566456<lat<30.648569，93.296329<lng<95.834171
-昌都经纬度：30.393039<lat<32.037207，95.702335<lng<98.459903

{northEastLat, northEastLng} 是右上（东北方向）
{southWestLat, southWestLng} 是左下（西南方向）

林芝:
northEastLat：30.648569
northEastLng: 95.834171
southWestLat: 28.566456
southWestLng: 93.296329

昌都:
northEastLat：32.037207
northEastLng: 98.459903
southWestLat: 30.393039
southWestLng: 95.702335

*/

const isInBounds = (lat, lng, bounds) => lat > bounds.southWestLat && lat < bounds.northEastLat && lng > bounds.southWestLng && lng < bounds.northEastLng;

// 坐标是否在边界线上，当前边界定义为东北、西南两个角，所以一定是个无倾斜的矩形
export const isLocOnBounds = (lat, lng, zoomLevel): boolean => {
  const bounds = MapLimitBoundsWithRegion('', lat, lng);
  if (bounds) {
    // 为了避免较高zoom level下，距离500m仍判定为在边界线，导致用户理解困难，通过zoom level平滑距离判定。
    const radio = Math.max(0.05, (1 - (Math.min(zoomLevel, MAX_ZOOM_LEVEL) - MIN_ZOOM_LEVEL) / (MAX_ZOOM_LEVEL - MIN_ZOOM_LEVEL)));
    const latThreshold = radio * 0.005; // 0.005大约是500m,因围栏和实际踩点经纬度会有偏差，实测大概是500ms以内
    const lngThreshold = (radio + 0.02) * 0.015; // 经度的偏差会略大，这里是调试中的经验值
    return Math.abs(lat - bounds.southWest.latitude) < latThreshold
                || Math.abs(lng - bounds.southWest.longitude) < lngThreshold
                || Math.abs(lat - bounds.northEast.latitude) < latThreshold
                || Math.abs(lng - bounds.northEast.longitude) < lngThreshold;
  } else {
    return false;
  }
};

const checkInTestAreaBounds = (lat = 0, lng = 0) => {
  // 定义测试区域的边界
  const HKBounds = {
    southWestLat: 28.566456,
    southWestLng: 93.296329,
    northEastLat: 30.648569,
    northEastLng: 95.834171
  };
  const SABounds = {
    southWestLat: 30.393039,
    southWestLng: 95.702335,
    northEastLat: 32.037207,
    northEastLng: 98.459903
  };
  const MGBounds = {
    southWestLat: -25.97,
    southWestLng: 41.96,
    northEastLat: -11.31,
    northEastLng: 51.52
  };

  const boundsArray = [HKBounds, SABounds, MGBounds];

  for (const bounds of boundsArray) {
    if (isInBounds(lat, lng, bounds)) {
      return true;
    }
  }
  return false;
};

/**
 * 是否开启地址围栏
 */
const isOpenAddressFence = () => {
  const SLCompass = Mach.requireModule('SLCompass');
  const regionInfo = SLCompass?.getRegionInfo?.() || {};
  return regionInfo.openSARegion?.homePage || false;
};

export const minZoomLevel = () => {
  if (Mach.env.isDebug) {
    return 2;
  }
  if (Mach.env.isIOS) {
    const SLMap = Mach.requireModule('SLMapModule');
    const mapLimitBounds = SLMap?.getLimitBounds ? SLMap.getLimitBounds() : {};
    const level = mapLimitBounds?.minZoomLevel ?? 10;
    return level;
  } else {
    return 2;
  }
};

export const cameraLimitMode = () => {
  const mode: MapCameraFitMode = MapCameraFitMode.FitHeight;
  return mode;
};

function istInsideRectangle(
  point: MapLatLng,
  northeast: MapLatLng,
  southwest: MapLatLng
): boolean {
  // 检查纬度是否在矩形的南北边界内
  const isLatitudeInside = point.latitude >= southwest.latitude && point.latitude <= northeast.latitude;
  // 检查经度是否在矩形的西东边界内
  let isLongitudeInside;
  if (northeast.longitude >= southwest.longitude) {
    // 矩形不跨越180°经线
    isLongitudeInside = point.longitude >= southwest.longitude && point.longitude <= northeast.longitude;
  } else {
    // 矩形跨越180°经线
    isLongitudeInside = point.longitude >= southwest.longitude || point.longitude <= northeast.longitude;
  }
  // console.log("northeast",northeast)
  // console.log("southwest",southwest)
  // console.log("isLatitudeInside",isLatitudeInside)
  // console.log("isLongitudeInside",isLongitudeInside)
  return isLatitudeInside && isLongitudeInside;
}

export function isPointInsideRectangle(
  point: MapLatLng,
  bounds?: MapLatLngBounds
) {
  // let northEast: MapLatLng = {latitude:NorthEastLat,longitude:NorthEastLng};
  // let southWest: MapLatLng = {latitude:SouthWestLat,longitude:SouthWestLng};
  // let bounds: MapLatLngBounds = {northEast:northEast,southWest:southWest};

  console.log(point);
  const { latitude: NorthEastLat, longitude: NorthEastLng } = bounds?.northEast || {};
  const { latitude: SouthWestLat, longitude: SouthWestLng } = bounds?.southWest || {};

  let isInside: boolean = false;
  if (bounds?.northEast && bounds?.southWest) {
    isInside = istInsideRectangle(point, bounds?.northEast, bounds?.southWest);
  }
  console.log(isInside ? '点在矩形内' : '点不在矩形内');
  return isInside;
}
